import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
  Chip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link as LinkIcon,
  FormatQuote,
  FormatColorText,
  Title as TitleIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Code as CodeIcon,
  HorizontalRule as HorizontalRuleIcon,
  Save as SaveIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  ArticleOutlined as ArticleOutlinedIcon,
  DynamicFormOutlined as DynamicFormOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Public as PublicIcon,
  EditNote as EditNoteIcon,
  RestartAlt as RestartAltIcon,
  Refresh as RefreshIcon,
  Link as LinkIconAlias,
  OpenInNew as OpenInNewIcon,
  TableChart as TableChartIcon,
  FormatColorFill,
  CompareArrows as CompareArrowsIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { getCustomPages, saveCustomPage, deleteCustomPage, CustomPageData } from '../../../services/ecommerceApi';
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { withFavyoflexAdvantagesHero } from '../../../utils/favyoflexPageHero';
import { ensureFavyoflexAdvantageSubpages } from '../utils/favyoflexPanelPages';
import FormPickerDialog from './FormPickerDialog';
import GuideButton from '../../common/guide/GuideButton';
import SimpleProductSelectionModal from './SimpleProductSelectionModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';

// =============================================================================
// Types
// =============================================================================

interface PagesSettingsProps {
  companyId: string;
  configId: string;
}

interface PageItem {
  id: string;
  configId: string;
  title: string;
  slug: string;
  content: string;
  metaTitle: string;
  metaDescription: string;
  isActive: boolean;
  displayOrder: number;
  showInMenu: boolean;
  switcherGroup: string;
  subtitle: string;
  bannerImageUrl: string;
  bannerHeight: number;
  isDirty?: boolean;
  isNew?: boolean;
}

interface ImageCardItem {
  imageUrl: string;
  title?: string;
  subtitle?: string;
  description?: string;
  linkUrl?: string;
  productId?: string;
  widthPx?: number;
  heightPx?: number;
}

interface ImageCardsSettings {
  layout?: 'grid' | 'sideText';
  sideTextImageAlign?: 'left' | 'right';
}

interface ImageCardsPayload {
  title?: string;
  cards: ImageCardItem[];
  settings?: ImageCardsSettings;
}

interface ImageCardsDialogState {
  open: boolean;
  isEdit: boolean;
  title: string;
  layout: 'grid' | 'sideText';
  sideTextImageAlign: 'left' | 'right';
  cards: ImageCardItem[];
}

interface EcommerceProductLite {
  id: string;
  name: string;
  stockCode: string;
  imageUrl?: string | null;
}

const DEFAULT_IMAGE_CARD: ImageCardItem = { imageUrl: '', title: '', subtitle: '', description: '', linkUrl: '' };
const DEFAULT_SIDE_TEXT_IMAGE_SIZE = { widthPx: 320, heightPx: 200 };

const DEFAULT_IMAGE_CARDS_DIALOG: ImageCardsDialogState = {
  open: false,
  isEdit: false,
  title: '',
  layout: 'grid',
  sideTextImageAlign: 'left',
  cards: [DEFAULT_IMAGE_CARD]
};

const escapeHtml = (value: string): string =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildImageCardsEditorPlaceholder = (encoded: string, payload: ImageCardsPayload): string => {
  const cards = Array.isArray(payload.cards) ? payload.cards : [];
  const layout = payload.settings?.layout === 'sideText' ? 'sideText' : 'grid';
  const layoutLabel = layout === 'sideText' ? 'Görsel + Metin' : 'Görsel Kartlar';
  const safeTitle = escapeHtml(payload.title || layoutLabel);
  const thumbs = cards
    .map(
      (card) => {
        const widthPx = Number(card.widthPx);
        const heightPx = Number(card.heightPx);

        if (layout === 'sideText') {
          const effectiveW =
            Number.isFinite(widthPx) && widthPx > 0 ? Math.round(widthPx) : DEFAULT_SIDE_TEXT_IMAGE_SIZE.widthPx;
          const effectiveH =
            Number.isFinite(heightPx) && heightPx > 0 ? Math.round(heightPx) : DEFAULT_SIDE_TEXT_IMAGE_SIZE.heightPx;
          const sizeStyle = ` style="width:${effectiveW}px;height:${effectiveH}px;"`;

          return (
            `<div class="gts-image-cards-side-preview">
              <figure class="gts-image-cards-thumb"${sizeStyle}>
                <img src="${escapeHtml(card.imageUrl || '')}" alt="${escapeHtml(card.title || 'Gorsel')}" loading="lazy" />
              </figure>
              <div class="gts-image-cards-side-copy">
                <strong>${escapeHtml(card.title || 'Başlık')}</strong>
                <p>${escapeHtml(card.description || card.subtitle || 'Açıklama metni')}</p>
              </div>
            </div>`
          );
        }

        const sizeStyle =
          Number.isFinite(widthPx) && widthPx > 0 && Number.isFinite(heightPx) && heightPx > 0
            ? ` style="width:${Math.round(widthPx)}px;height:${Math.round(heightPx)}px;"`
            : '';

        return (
          `<figure class="gts-image-cards-thumb"${sizeStyle}>
            <img src="${escapeHtml(card.imageUrl || '')}" alt="${escapeHtml(card.title || 'Gorsel')}" loading="lazy" />
          </figure>`
        );
      }
    )
    .join('');

  const thumbsClass =
    layout === 'sideText' ? 'gts-image-cards-thumbs gts-image-cards-thumbs--side-text' : 'gts-image-cards-thumbs';

  return `<p class="gts-image-cards-caret-anchor"><br></p>
  <section class="gts-image-cards-placeholder" data-gts-image-cards="${encoded}" contenteditable="false">
    <div class="gts-image-cards-header">
      <span>🖼️ ${safeTitle} (${cards.length} kart)</span>
      <div class="gts-image-cards-actions">
        <button type="button" class="gts-image-cards-edit" title="Kart bloğunu düzenle">Düzenle</button>
        <button type="button" class="gts-image-cards-remove" title="Kart bloğunu sil">Sil</button>
      </div>
    </div>
    <div class="${thumbsClass}">${thumbs}</div>
  </section>
  <p class="gts-image-cards-caret-anchor"><br></p>`;
};

const normalizeImageCardsForEditor = (inputHtml: string): string => {
  if (!inputHtml) return inputHtml;
  let output = inputHtml;

  // Attribute değeri çift tırnaklı olsa bile içinde kesme işareti (')
  // bulunabilir (encodeURIComponent apostrofu kodlamaz). Açılış tırnağını
  // yakalayıp aynı tırnağa kadar (\1/\2) eşleştiriyoruz; [^"']+ apostrofta
  // erken kesip encoded JSON'u bozuyordu.
  output = output.replace(
    /<section[^>]*data-gts-image-cards=(["'])([\s\S]*?)\1[^>]*>[\s\S]*?<\/section>/gi,
    (_full, _quote, encodedJson) => {
      try {
        const decoded = decodeURIComponent(String(encodedJson || ''));
        const parsed = JSON.parse(decoded || '{}') as ImageCardsPayload;
        if (!Array.isArray(parsed.cards) || parsed.cards.length === 0) return _full;
        return buildImageCardsEditorPlaceholder(encodedJson, parsed);
      } catch {
        return _full;
      }
    }
  );

  output = output.replace(
    /<(span|div|p)[^>]*data-gts-image-cards=(["'])([\s\S]*?)\2[^>]*>[\s\S]*?<\/\1>/gi,
    (_full, _tag, _quote, encodedJson) => {
      try {
        const decoded = decodeURIComponent(String(encodedJson || ''));
        const parsed = JSON.parse(decoded || '{}') as ImageCardsPayload;
        if (!Array.isArray(parsed.cards) || parsed.cards.length === 0) return _full;
        return buildImageCardsEditorPlaceholder(encodedJson, parsed);
      } catch {
        return _full;
      }
    }
  );

  output = output.replace(/\[\[imagecards:([\s\S]+?)\]\]/gi, (_full, encodedJson) => {
    try {
      const decoded = decodeURIComponent(String(encodedJson || ''));
      const parsed = JSON.parse(decoded || '{}') as ImageCardsPayload;
      if (!Array.isArray(parsed.cards) || parsed.cards.length === 0) return _full;
      return buildImageCardsEditorPlaceholder(encodedJson, parsed);
    } catch {
      return _full;
    }
  });

  return output;
};

// =============================================================================
// Helpers
// =============================================================================

const slugify = (input: string): string => {
  if (!input) return '';
  const replacements: Record<string, string> = {
    ı: 'i', İ: 'i', ş: 's', Ş: 's', ç: 'c', Ç: 'c',
    ğ: 'g', Ğ: 'g', ü: 'u', Ü: 'u', ö: 'o', Ö: 'o'
  };
  let s = input.toLowerCase();
  s = s.split('').map((ch) => replacements[ch] ?? ch).join('');
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/^-+|-+$/g, '');
  return s;
};

const fromApi = (raw: any, companyId?: string | null): PageItem => withFavyoflexAdvantagesHero(companyId, {
  id: String(raw?.id ?? ''),
  configId: String(raw?.configId ?? ''),
  title: String(raw?.title ?? ''),
  slug: String(raw?.slug ?? ''),
  content: normalizeImageCardsForEditor(String(raw?.content ?? '')),
  metaTitle: String(raw?.metaTitle ?? ''),
  metaDescription: String(raw?.metaDescription ?? ''),
  isActive: raw?.isActive !== false,
  displayOrder: Number(raw?.displayOrder ?? 0),
  showInMenu: raw?.showInMenu === true || raw?.ShowInMenu === true,
  switcherGroup: String(raw?.name ?? raw?.Name ?? '').trim(),
  subtitle: String(raw?.subtitle ?? raw?.Subtitle ?? ''),
  bannerImageUrl: String(raw?.bannerImageUrl ?? raw?.BannerImageUrl ?? ''),
  bannerHeight: Number(raw?.bannerHeight ?? raw?.BannerHeight ?? 380) || 380,
});

// =============================================================================
// Styled Tabs (Hakkımızda / Hizmetler ekranlarındaki trapez stil)
// =============================================================================

const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    display: 'none'
  }
});

const StyledTab = styled((props: { label: React.ReactNode; value?: number }) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(14),
  padding: theme.spacing(1, 3.5),
  minHeight: 'auto',
  minWidth: '150px',
  color: theme.palette.text.secondary,
  position: 'relative',
  backgroundColor: theme.palette.grey[200],
  clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
  marginRight: theme.spacing(-2),
  '&:hover': {
    backgroundColor: theme.palette.grey[300]
  },
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.background.paper,
    zIndex: 2
  }
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`pages-tabpanel-${index}`}
    aria-labelledby={`pages-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

// =============================================================================
// Rich Text Editor (hafif, document.execCommand tabanlı)
// =============================================================================

interface RichTextEditorProps {
  value: string;
  onChange: (next: string) => void;
  extraToolbarItems?: React.ReactNode;
  onImageCardsEdit?: (encoded: string, placeholder: HTMLElement) => void;
}

const FONT_FAMILY_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Varsayılan', value: 'inherit' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' }
];

const FONT_SIZE_OPTIONS: Array<{ label: string; value: string; commandValue: string }> = [
  { label: '12 px', value: '12', commandValue: '2' },
  { label: '14 px', value: '14', commandValue: '3' },
  { label: '16 px', value: '16', commandValue: '4' },
  { label: '18 px', value: '18', commandValue: '5' },
  { label: '24 px', value: '24', commandValue: '6' },
  { label: '32 px', value: '32', commandValue: '7' }
];

const EDITOR_COLORS = [
  '#000000', '#374151', '#6b7280', '#9ca3af',
  '#ef4444', '#f97316', '#f59e0b', '#10b981',
  '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'
];

const TABLE_CELL_STYLE = 'padding: 8px; border: 1px solid #d1d5db;';

const closestElement = (node: Node | null): HTMLElement | null => {
  if (!node) return null;
  return node instanceof HTMLElement ? node : node.parentElement;
};

const closestTableCell = (node: Node | null, editor: HTMLElement | null): HTMLTableCellElement | null => {
  const el = closestElement(node);
  const cell = el?.closest('td, th') as HTMLTableCellElement | null;
  if (!cell || !editor || !editor.contains(cell)) return null;
  return cell;
};

const closestTable = (node: Node | null, editor: HTMLElement | null): HTMLTableElement | null => {
  const el = closestElement(node);
  const table = el?.closest('table') as HTMLTableElement | null;
  if (!table || !editor || !editor.contains(table)) return null;
  return table;
};

const createTableCell = (tag: 'td' | 'th', text = ''): HTMLTableCellElement => {
  const cell = document.createElement(tag);
  cell.setAttribute('style', tag === 'th' ? `${TABLE_CELL_STYLE} background-color: #f8fafc;` : TABLE_CELL_STYLE);
  cell.innerHTML = text || '<br>';
  return cell;
};

const buildTableHtml = (rows: number, cols: number): string => {
  const safeRows = Math.max(1, Math.min(20, rows));
  const safeCols = Math.max(1, Math.min(10, cols));
  const header = Array.from({ length: safeCols }, (_, i) => (
    `<th style="${TABLE_CELL_STYLE} background-color: #f8fafc;">Başlık ${i + 1}</th>`
  )).join('');
  const bodyRows = Array.from({ length: safeRows }, () => (
    `<tr>${Array.from({ length: safeCols }, () => `<td style="${TABLE_CELL_STYLE}"><br></td>`).join('')}</tr>`
  )).join('');
  return (
    `<table class="gts-page-table" style="border-collapse: collapse; width: 100%; margin: 16px 0;">` +
    `<thead><tr>${header}</tr></thead>` +
    `<tbody>${bodyRows}</tbody>` +
    `</table><p><br></p>`
  );
};

const applyColorToRangeTextNodes = (range: Range, color: string) => {
  const ancestor = range.commonAncestorContainer;
  const root = ancestor.nodeType === Node.TEXT_NODE ? ancestor.parentNode : ancestor;
  if (!root) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Array<{ node: Text; start: number; end: number }> = [];
  let current: Node | null = walker.nextNode();
  while (current) {
    const text = current as Text;
    if (range.intersectsNode(text)) {
      const value = text.nodeValue || '';
      let start = 0;
      let end = value.length;
      if (text === range.startContainer) start = range.startOffset;
      if (text === range.endContainer) end = range.endOffset;
      if (start < end) targets.push({ node: text, start, end });
    }
    current = walker.nextNode();
  }

  for (let i = targets.length - 1; i >= 0; i -= 1) {
    const { node, start, end } = targets[i];
    let working = node;
    if (end < (working.nodeValue || '').length) working.splitText(end);
    if (start > 0) working = working.splitText(start);

    const parent = working.parentElement;
    if (parent && parent.tagName === 'SPAN' && parent.childNodes.length === 1) {
      parent.style.color = color;
      continue;
    }
    const span = document.createElement('span');
    span.style.color = color;
    working.parentNode?.insertBefore(span, working);
    span.appendChild(working);
  }
};

export interface RichTextEditorHandle {
  insertHtml: (html: string) => void;
  focus: () => void;
  replaceImageCardsBlock: (placeholder: HTMLElement, html: string) => void;
}

const RichTextEditor = React.forwardRef<RichTextEditorHandle, RichTextEditorProps>(({ value, onChange, extraToolbarItems, onImageCardsEdit }, ref) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const isTypingRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  const colorPickerRef = useRef<HTMLDivElement | null>(null);
  const activeCellRef = useRef<HTMLTableCellElement | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customTextColor, setCustomTextColor] = useState('#000000');
  const [headingAnchor, setHeadingAnchor] = useState<null | HTMLElement>(null);
  const [fontFamily, setFontFamily] = useState('inherit');
  const [fontSize, setFontSize] = useState('14');
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableToolbarOpen, setTableToolbarOpen] = useState(false);

  const preventBlur = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const selection = window.getSelection();
    if (!selection) return;
    const saved = savedRangeRef.current;
    if (saved && el.contains(saved.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(saved);
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  React.useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    insertHtml: (html: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const beforeHtml = el.innerHTML || '';
      const selection = window.getSelection();
      const containsSelection = selection && selection.rangeCount > 0
        && el.contains(selection.anchorNode);
      if (!containsSelection) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      const ok = document.execCommand('insertHTML', false, html);
      const afterHtml = el.innerHTML || '';
      // Bazı tarayıcılarda execCommand false döner ama içerik yine de eklenmiş olur.
      // Sadece gerçekten eklenmediyse fallback ile manuel append yap.
      if (!ok && afterHtml === beforeHtml) {
        el.innerHTML = (el.innerHTML || '') + html;
      }
      onChange(el.innerHTML);
    },
    replaceImageCardsBlock: (placeholder: HTMLElement, html: string) => {
      const el = editorRef.current;
      if (!el || !el.contains(placeholder)) return;

      const prev = placeholder.previousElementSibling as HTMLElement | null;
      const next = placeholder.nextElementSibling as HTMLElement | null;
      const startNode = prev?.classList.contains('gts-image-cards-caret-anchor') ? prev : placeholder;
      const endNode = next?.classList.contains('gts-image-cards-caret-anchor') ? next : placeholder;

      const range = document.createRange();
      range.setStartBefore(startNode);
      range.setEndAfter(endNode);
      range.deleteContents();
      range.insertNode(range.createContextualFragment(html));

      isTypingRef.current = true;
      onChange(el.innerHTML);
    }
  }), [onChange]);

  useEffect(() => {
    if (editorRef.current && !isTypingRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isTypingRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!showColorPicker) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && colorPickerRef.current?.contains(target)) return;
      setShowColorPicker(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showColorPicker]);

  const exec = (command: string, val?: string) => {
    if (!editorRef.current) return;
    restoreSelection();
    if (command === 'foreColor' || command === 'fontName' || command === 'fontSize') {
      document.execCommand('styleWithCSS', false, 'true');
    }
    document.execCommand(command, false, val);
    isTypingRef.current = true;
    onChange(editorRef.current.innerHTML);
    saveSelection();
  };

  const syncTableToolbar = () => {
    const cell = closestTableCell(
      window.getSelection()?.anchorNode || savedRangeRef.current?.startContainer || null,
      editorRef.current
    );
    activeCellRef.current = cell;
    setTableToolbarOpen(Boolean(cell));
  };

  const emitEditorChange = () => {
    const el = editorRef.current;
    if (!el) return;
    isTypingRef.current = true;
    onChange(el.innerHTML);
    saveSelection();
    syncTableToolbar();
  };

  const applyForeColor = (color: string, options?: { keepOpen?: boolean }) => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    setCustomTextColor(color);

    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const cell = closestTableCell(range?.commonAncestorContainer || selection?.anchorNode || null, el);

    if (cell && (!range || range.collapsed || range.toString().trim() === (cell.innerText || '').trim())) {
      cell.style.color = color;
      cell.querySelectorAll<HTMLElement>('*').forEach((node) => {
        if (node.style?.color) node.style.color = color;
        if (node.tagName === 'FONT') {
          node.setAttribute('color', color);
          node.style.color = color;
        }
      });
    } else if (range && !range.collapsed) {
      applyColorToRangeTextNodes(range, color);
    } else {
      document.execCommand('styleWithCSS', false, 'true');
      document.execCommand('foreColor', false, color);
    }

    emitEditorChange();
    if (!options?.keepOpen) setShowColorPicker(false);
  };

  const applyCellBackground = (color: string) => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    const cell = activeCellRef.current && el.contains(activeCellRef.current)
      ? activeCellRef.current
      : closestTableCell(window.getSelection()?.anchorNode || null, el);
    if (!cell) return;
    cell.style.backgroundColor = color;
    emitEditorChange();
  };

  const insertTableAtCaret = () => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    const html = buildTableHtml(tableRows, tableCols);
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const tableNode = wrapper.firstElementChild;
    const afterNode = wrapper.children[1] || null;
    if (!tableNode) return;

    const selection = window.getSelection();
    const start = selection?.anchorNode || savedRangeRef.current?.startContainer || null;
    const existingTable = closestTable(start, el);
    const startEl = closestElement(start);
    const block = startEl?.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote, pre') as HTMLElement | null;

    if (existingTable) {
      existingTable.after(tableNode);
    } else if (block && el.contains(block) && block !== el) {
      block.after(tableNode);
    } else {
      el.appendChild(tableNode);
    }
    if (afterNode) tableNode.after(afterNode);

    const firstBodyCell = (tableNode as HTMLTableElement).querySelector('td');
    if (firstBodyCell) {
      const range = document.createRange();
      range.selectNodeContents(firstBodyCell);
      range.collapse(true);
      selection?.removeAllRanges();
      selection?.addRange(range);
      savedRangeRef.current = range.cloneRange();
    }

    setTableDialogOpen(false);
    emitEditorChange();
  };

  const runTableAction = (action: 'addRow' | 'deleteRow' | 'addCol' | 'deleteCol' | 'toggleHeader' | 'deleteTable') => {
    const el = editorRef.current;
    const cell = activeCellRef.current && el?.contains(activeCellRef.current)
      ? activeCellRef.current
      : closestTableCell(window.getSelection()?.anchorNode || null, el || null);
    const table = cell ? closestTable(cell, el || null) : closestTable(window.getSelection()?.anchorNode || null, el || null);
    if (!el || !table) return;

    const row = cell?.parentElement as HTMLTableRowElement | null;
    const rowIndex = row?.rowIndex ?? table.rows.length - 1;
    const cellIndex = cell?.cellIndex ?? 0;

    if (action === 'deleteTable') {
      table.remove();
      activeCellRef.current = null;
      setTableToolbarOpen(false);
      emitEditorChange();
      return;
    }

    if (action === 'addRow') {
      const refRow = table.rows[rowIndex] || table.rows[table.rows.length - 1];
      const newRow = table.insertRow(rowIndex + 1);
      const colCount = refRow?.cells.length || table.rows[0]?.cells.length || 1;
      for (let i = 0; i < colCount; i += 1) {
        newRow.appendChild(createTableCell('td'));
      }
    } else if (action === 'deleteRow') {
      if (table.rows.length <= 1) {
        table.remove();
        activeCellRef.current = null;
        setTableToolbarOpen(false);
        emitEditorChange();
        return;
      }
      table.deleteRow(rowIndex);
    } else if (action === 'addCol') {
      Array.from(table.rows).forEach((currentRow) => {
        const tag = currentRow.cells[0]?.tagName === 'TH' ? 'th' : 'td';
        const next = createTableCell(tag as 'td' | 'th');
        const refCell = currentRow.cells[cellIndex] || null;
        if (refCell) refCell.after(next);
        else currentRow.appendChild(next);
      });
    } else if (action === 'deleteCol') {
      const colCount = table.rows[0]?.cells.length || 0;
      if (colCount <= 1) {
        table.remove();
        activeCellRef.current = null;
        setTableToolbarOpen(false);
        emitEditorChange();
        return;
      }
      Array.from(table.rows).forEach((currentRow) => {
        if (currentRow.cells[cellIndex]) currentRow.deleteCell(cellIndex);
      });
    } else if (action === 'toggleHeader') {
      const firstRow = table.rows[0];
      if (!firstRow) return;
      const makeHeader = firstRow.cells[0]?.tagName !== 'TH';
      Array.from(firstRow.cells).forEach((currentCell, index) => {
        const next = createTableCell(makeHeader ? 'th' : 'td', currentCell.innerHTML);
        if (!makeHeader) next.style.backgroundColor = '';
        firstRow.replaceChild(next, currentCell);
        if (index === 0) activeCellRef.current = next;
      });
    }

    emitEditorChange();
  };

  const applyFontFamily = (nextFontFamily: string) => {
    setFontFamily(nextFontFamily);
    if (nextFontFamily === 'inherit') {
      exec('removeFormat');
      return;
    }
    // Browser'larda font komutunun CSS olarak uygulanması için styleWithCSS zorlanır.
    document.execCommand('styleWithCSS', false, 'true');
    exec('fontName', nextFontFamily);
  };

  const applyFontSize = (nextFontSize: string) => {
    setFontSize(nextFontSize);
    const target = FONT_SIZE_OPTIONS.find((option) => option.value === nextFontSize);
    if (!target) return;
    document.execCommand('styleWithCSS', false, 'true');
    exec('fontSize', target.commandValue);
  };

  const syncImageCardsPayloadFromEditor = (): boolean => {
    const el = editorRef.current;
    if (!el) return false;

    let hasAnyChange = false;
    const placeholders = el.querySelectorAll<HTMLElement>('.gts-image-cards-placeholder[data-gts-image-cards]');
    placeholders.forEach((placeholder) => {
      const encoded = placeholder.getAttribute('data-gts-image-cards');
      if (!encoded) return;

      try {
        const decoded = decodeURIComponent(encoded);
        const parsed = JSON.parse(decoded) as ImageCardsPayload;
        if (!Array.isArray(parsed.cards) || parsed.cards.length === 0) return;

        const thumbElements = placeholder.querySelectorAll<HTMLElement>('.gts-image-cards-thumb');
        let placeholderChanged = false;
        const nextCards = parsed.cards.map((card, index) => {
          const thumb = thumbElements[index];
          if (!thumb) return card;

          const styleWidth = thumb.style.width?.trim();
          const styleHeight = thumb.style.height?.trim();
          const widthPx = styleWidth ? Number.parseInt(styleWidth, 10) : undefined;
          const heightPx = styleHeight ? Number.parseInt(styleHeight, 10) : undefined;

          if (Number.isFinite(widthPx) && widthPx! > 0 && Number.isFinite(heightPx) && heightPx! > 0) {
            if (card.widthPx !== widthPx || card.heightPx !== heightPx) {
              placeholderChanged = true;
              return { ...card, widthPx, heightPx };
            }
          }
          return card;
        });

        if (placeholderChanged) {
          hasAnyChange = true;
          const nextPayload: ImageCardsPayload = { ...parsed, cards: nextCards };
          placeholder.setAttribute('data-gts-image-cards', encodeURIComponent(JSON.stringify(nextPayload)));
        }
      } catch {
        // ignore malformed payload
      }
    });

    return hasAnyChange;
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    syncImageCardsPayloadFromEditor();
    isTypingRef.current = true;
    onChange(editorRef.current.innerHTML);
  };

  const handleEditorMouseUp = () => {
    saveSelection();
    syncTableToolbar();
    if (!editorRef.current) return;
    const changed = syncImageCardsPayloadFromEditor();
    if (changed) {
      isTypingRef.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleEditorMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target || !editorRef.current) return;

    const editButton = target.closest('.gts-image-cards-edit');
    if (editButton) {
      event.preventDefault();
      event.stopPropagation();

      const placeholder = editButton.closest('.gts-image-cards-placeholder') as HTMLElement | null;
      const encoded = placeholder?.getAttribute('data-gts-image-cards');
      if (placeholder && encoded && onImageCardsEdit) {
        onImageCardsEdit(encoded, placeholder);
      }
      return;
    }

    const removeButton = target.closest('.gts-image-cards-remove');
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();

    const placeholder = removeButton.closest('.gts-image-cards-placeholder');
    if (placeholder) {
      const prev = placeholder.previousElementSibling as HTMLElement | null;
      const next = placeholder.nextElementSibling as HTMLElement | null;
      if (prev && prev.classList.contains('gts-image-cards-caret-anchor')) {
        prev.remove();
      }
      if (next && next.classList.contains('gts-image-cards-caret-anchor')) {
        next.remove();
      }
      placeholder.remove();
      isTypingRef.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = window.prompt('Bağlantı URL\'si:', 'https://');
    if (url) exec('createLink', url);
  };

  const handleHeading = (tag: string) => {
    setHeadingAnchor(null);
    if (tag === 'p') exec('formatBlock', 'P');
    else exec('formatBlock', tag.toUpperCase());
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#fff'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          p: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#f8fafc'
        }}
      >
        <Tooltip title="Başlık">
          <IconButton
            size="small"
            onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
            onClick={(e) => setHeadingAnchor(e.currentTarget)}
          >
            <TitleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={headingAnchor}
          open={Boolean(headingAnchor)}
          onClose={() => setHeadingAnchor(null)}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableAutoFocusItem
          MenuListProps={{ onMouseDown: preventBlur }}
        >
          <MenuItem onClick={() => handleHeading('h1')}><Typography variant="h5" sx={{ m: 0 }}>Başlık 1</Typography></MenuItem>
          <MenuItem onClick={() => handleHeading('h2')}><Typography variant="h6" sx={{ m: 0 }}>Başlık 2</Typography></MenuItem>
          <MenuItem onClick={() => handleHeading('h3')}><Typography sx={{ fontWeight: 700 }}>Başlık 3</Typography></MenuItem>
          <MenuItem onClick={() => handleHeading('h4')}><Typography sx={{ fontWeight: 600 }}>Başlık 4</Typography></MenuItem>
          <MenuItem onClick={() => handleHeading('p')}>Normal metin</MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <TextField
          select
          size="small"
          value={fontFamily}
          onMouseDown={() => saveSelection()}
          onChange={(event) => applyFontFamily(String(event.target.value || 'inherit'))}
          sx={{
            minWidth: 145,
            '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
          }}
        >
          {FONT_FAMILY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontFamily: option.value === 'inherit' ? 'inherit' : option.value }}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          value={fontSize}
          onMouseDown={() => saveSelection()}
          onChange={(event) => applyFontSize(String(event.target.value || '14'))}
          sx={{
            minWidth: 90,
            '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
          }}
        >
          {FONT_SIZE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Kalın"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('bold')}><FormatBold fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="İtalik"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('italic')}><FormatItalic fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Altı çizili"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('underline')}><FormatUnderlined fontSize="small" /></IconButton></Tooltip>

        <Box sx={{ position: 'relative' }} ref={colorPickerRef}>
          <Tooltip title="Yazı rengi">
            <IconButton
              size="small"
              onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
              onClick={() => setShowColorPicker((open) => !open)}
            >
              <FormatColorText fontSize="small" />
            </IconButton>
          </Tooltip>
          {showColorPicker && (
            <Paper
              elevation={4}
              onMouseDown={preventBlur}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 20,
                p: 1.25,
                width: 220
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 600 }}>
                Yazı rengi
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, mb: 1 }}>
                {EDITOR_COLORS.map((c) => (
                  <Box
                    key={c}
                    onMouseDown={preventBlur}
                    onClick={() => applyForeColor(c)}
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', backgroundColor: c,
                      cursor: 'pointer', border: '1px solid rgba(0,0,0,0.12)',
                      outline: customTextColor === c ? '2px solid #6366f1' : 'none',
                      outlineOffset: 1
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="input"
                  type="color"
                  value={customTextColor}
                  onMouseDown={(e: React.MouseEvent) => { preventBlur(e); saveSelection(); }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => applyForeColor(e.target.value, { keepOpen: true })}
                  sx={{
                    width: 36, height: 28, p: 0, border: '1px solid #ccc',
                    borderRadius: 0.5, cursor: 'pointer', background: 'none'
                  }}
                />
                <Typography variant="caption" color="text.secondary">Özel renk</Typography>
              </Box>
            </Paper>
          )}
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Madde işaretli liste"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('insertUnorderedList')}><FormatListBulleted fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Numaralı liste"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('insertOrderedList')}><FormatListNumbered fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Alıntı"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('formatBlock', 'BLOCKQUOTE')}><FormatQuote fontSize="small" /></IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Sola hizala"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('justifyLeft')}><FormatAlignLeft fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Ortala"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('justifyCenter')}><FormatAlignCenter fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Sağa hizala"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('justifyRight')}><FormatAlignRight fontSize="small" /></IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Bağlantı ekle"><IconButton size="small" onMouseDown={preventBlur} onClick={handleLink}><LinkIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Tablo ekle">
          <IconButton
            size="small"
            onMouseDown={(e) => { preventBlur(e); saveSelection(); }}
            onClick={() => setTableDialogOpen(true)}
          >
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Yatay çizgi"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('insertHorizontalRule')}><HorizontalRuleIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Kod (preformat)"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('formatBlock', 'PRE')}><CodeIcon fontSize="small" /></IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Geri al"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('undo')}><UndoIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Yinele"><IconButton size="small" onMouseDown={preventBlur} onClick={() => exec('redo')}><RedoIcon fontSize="small" /></IconButton></Tooltip>

        {extraToolbarItems && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {extraToolbarItems}
          </>
        )}
      </Box>

      {tableToolbarOpen && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#eef2ff'
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#3730a3', mr: 0.5 }}>
            Tablo
          </Typography>
          <Button size="small" onMouseDown={preventBlur} onClick={() => runTableAction('addRow')} sx={{ py: 0.1, minWidth: 0 }}>
            + Satır
          </Button>
          <Button size="small" onMouseDown={preventBlur} onClick={() => runTableAction('deleteRow')} sx={{ py: 0.1, minWidth: 0 }}>
            − Satır
          </Button>
          <Button size="small" onMouseDown={preventBlur} onClick={() => runTableAction('addCol')} sx={{ py: 0.1, minWidth: 0 }}>
            + Sütun
          </Button>
          <Button size="small" onMouseDown={preventBlur} onClick={() => runTableAction('deleteCol')} sx={{ py: 0.1, minWidth: 0 }}>
            − Sütun
          </Button>
          <Button size="small" onMouseDown={preventBlur} onClick={() => runTableAction('toggleHeader')} sx={{ py: 0.1, minWidth: 0 }}>
            Başlık satırı
          </Button>
          <Tooltip title="Hücre arka plan rengi">
            <Box
              component="input"
              type="color"
              defaultValue="#f8fafc"
              onMouseDown={(e: React.MouseEvent) => { preventBlur(e); saveSelection(); }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => applyCellBackground(e.target.value)}
              sx={{
                width: 28, height: 24, p: 0, border: '1px solid #c7d2fe',
                borderRadius: 0.5, cursor: 'pointer', background: 'none', ml: 0.5
              }}
            />
          </Tooltip>
          <FormatColorFill fontSize="small" sx={{ color: '#6366f1' }} />
          <Button
            size="small"
            color="error"
            onMouseDown={preventBlur}
            onClick={() => runTableAction('deleteTable')}
            sx={{ py: 0.1, minWidth: 0, ml: 'auto' }}
          >
            Tabloyu sil
          </Button>
        </Box>
      )}

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onMouseUp={handleEditorMouseUp}
        onMouseDown={handleEditorMouseDown}
        onKeyUp={() => { saveSelection(); syncTableToolbar(); }}
        onInput={handleInput}
        onBlur={handleInput}
        sx={{
          minHeight: 340,
          p: 2,
          outline: 'none',
          fontSize: '15px',
          lineHeight: 1.7,
          color: '#111827',
          '& h1': { fontSize: '1.75rem', fontWeight: 700, mt: 2, mb: 1 },
          '& h2': { fontSize: '1.45rem', fontWeight: 700, mt: 2, mb: 1 },
          '& h3': { fontSize: '1.2rem', fontWeight: 700, mt: 1.5, mb: 1 },
          '& h4': { fontSize: '1.05rem', fontWeight: 600, mt: 1.5, mb: 0.75 },
          '& p': { mb: 1.25 },
          '& ul, & ol': { pl: 3, mb: 1.5 },
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            margin: '16px 0',
            tableLayout: 'fixed'
          },
          '& td, & th': {
            padding: '8px',
            border: '1px solid #d1d5db',
            minWidth: 48,
            verticalAlign: 'top',
            wordBreak: 'break-word'
          },
          '& th': {
            backgroundColor: '#f8fafc',
            fontWeight: 600
          },
          '& a': { color: '#2563eb', textDecoration: 'underline' },
          '& blockquote': {
            borderLeft: '4px solid #cbd5e1',
            pl: 2,
            color: '#475569',
            fontStyle: 'italic',
            my: 1.5
          },
          '& pre': {
            backgroundColor: '#f1f5f9',
            p: 1.5,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            whiteSpace: 'pre-wrap'
          },
          '& .gts-form-placeholder': {
            display: 'inline-block',
            padding: '6px 12px',
            margin: '6px 0',
            borderRadius: '6px',
            backgroundColor: '#eff6ff',
            color: '#1e3a8a',
            border: '1px dashed #3b82f6',
            fontWeight: 600,
            fontSize: '0.9rem',
            userSelect: 'all'
          },
          '& .gts-map-placeholder': {
            display: 'inline-block',
            padding: '6px 12px',
            margin: '6px 0',
            borderRadius: '6px',
            backgroundColor: '#ecfdf5',
            color: '#065f46',
            border: '1px dashed #10b981',
            fontWeight: 600,
            fontSize: '0.9rem',
            userSelect: 'all'
          },
          '& .gts-image-cards-placeholder': {
            display: 'block',
            padding: '10px',
            margin: '8px 0',
            borderRadius: '6px',
            backgroundColor: '#f5f3ff',
            color: '#5b21b6',
            border: '1px dashed #8b5cf6',
            userSelect: 'all'
          },
          '& .gts-image-cards-caret-anchor': {
            minHeight: '1.25rem',
            margin: '6px 0',
          },
          '& .gts-image-cards-placeholder .gts-image-cards-header': {
            fontWeight: 700,
            fontSize: '0.9rem',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          },
          '& .gts-image-cards-placeholder .gts-image-cards-actions': {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexShrink: 0
          },
          '& .gts-image-cards-placeholder .gts-image-cards-edit': {
            border: '1px solid #cbd5e1',
            backgroundColor: '#fff',
            color: '#475569',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            padding: '2px 8px',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#6366f1',
              color: '#4338ca',
              backgroundColor: '#eef2ff'
            }
          },
          '& .gts-image-cards-placeholder .gts-image-cards-remove': {
            border: '1px solid #cbd5e1',
            backgroundColor: '#fff',
            color: '#475569',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            padding: '2px 8px',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#ef4444',
              color: '#b91c1c',
              backgroundColor: '#fef2f2'
            }
          },
          '& .gts-image-cards-placeholder .gts-image-cards-thumbs': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'flex-start'
          },
          '& .gts-image-cards-placeholder .gts-image-cards-thumb': {
            width: '160px',
            height: '160px',
            minWidth: '96px',
            minHeight: '96px',
            maxWidth: '1400px',
            maxHeight: '900px',
            margin: 0,
            resize: 'both',
            overflow: 'hidden',
            borderRadius: '6px',
            border: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: '#fff',
            cursor: 'se-resize'
          },
          '& .gts-image-cards-placeholder .gts-image-cards-thumbs img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '0',
            border: '0',
            margin: 0
          },
          '& .gts-image-cards-thumbs--side-text': {
            flexDirection: 'column',
            gap: '12px',
            overflow: 'visible'
          },
          '& .gts-image-cards-side-preview': {
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: '100%',
            overflow: 'visible',
            padding: '8px',
            borderRadius: '6px',
            backgroundColor: '#fff',
            border: '1px solid rgba(0,0,0,0.06)'
          },
          '& .gts-image-cards-side-preview .gts-image-cards-thumb': {
            display: 'block',
            minWidth: '120px',
            minHeight: '90px',
            maxWidth: '900px',
            maxHeight: '700px',
            flexShrink: 0,
            resize: 'both',
            overflow: 'hidden',
            cursor: 'se-resize'
          },
          '& .gts-image-cards-side-copy': {
            flex: 1,
            minWidth: 0,
            '& strong': {
              display: 'block',
              fontSize: '0.95rem',
              marginBottom: '6px'
            },
            '& p': {
              margin: 0,
              fontSize: '0.85rem',
              color: '#64748b'
            }
          }
        }}
      />

      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Tablo Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                label="Satır sayısı"
                type="number"
                fullWidth
                value={tableRows}
                onChange={(e) => setTableRows(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                inputProps={{ min: 1, max: 20 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sütun sayısı"
                type="number"
                fullWidth
                value={tableCols}
                onChange={(e) => setTableCols(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))}
                inputProps={{ min: 1, max: 10 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={insertTableAtCaret}>Ekle</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
RichTextEditor.displayName = 'RichTextEditor';

// =============================================================================
// Main Component
// =============================================================================

const PagesSettings: React.FC<PagesSettingsProps> = ({ companyId, configId }) => {
  const theme = useTheme();
  const [pages, setPages] = useState<PageItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
    open: false,
    type: 'success',
    message: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; pageId: string | null; title: string }>({
    open: false,
    pageId: null,
    title: ''
  });
  const [slugTouched, setSlugTouched] = useState<Record<string, boolean>>({});
  const [formPicker, setFormPicker] = useState<{ open: boolean }>({ open: false });
  const [mapDialog, setMapDialog] = useState<{ open: boolean; url: string }>({ open: false, url: '' });
  const [imageCardsDialog, setImageCardsDialog] = useState<ImageCardsDialogState>(DEFAULT_IMAGE_CARDS_DIALOG);
  const imageCardsEditTargetRef = useRef<HTMLElement | null>(null);
  const [productPicker, setProductPicker] = useState<{ open: boolean }>({ open: false });
  const [imageLibraryPicker, setImageLibraryPicker] = useState<{ open: boolean; targetIndex: number }>({
    open: false,
    targetIndex: -1
  });
  const [heroImageUploading, setHeroImageUploading] = useState(false);
  const editorRef = useRef<RichTextEditorHandle | null>(null);

  const activePage = pages[activeIndex];

  const fetchPages = useCallback(async () => {
    if (!configId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getCustomPages(configId);
      const rawPages = await ensureFavyoflexAdvantageSubpages(configId, companyId, res?.data ?? []);
      const list: PageItem[] = rawPages
        .map((raw: any) => fromApi(raw, companyId))
        .sort((a: PageItem, b: PageItem) => a.displayOrder - b.displayOrder);
      setPages(list);
      setActiveIndex((prev) => Math.min(prev, Math.max(0, list.length - 1)));
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Sayfalar yüklenirken hata oluştu';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [configId, companyId]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const updateActivePage = (patch: Partial<PageItem>) => {
    setPages((prev) =>
      prev.map((p, idx) => (idx === activeIndex ? { ...p, ...patch, isDirty: true } : p))
    );
  };

  const uploadPageHeroImage = async (file: File) => {
    try {
      setHeroImageUploading(true);
      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (uploaded?.originalUrl) {
        updateActivePage({ bannerImageUrl: uploaded.originalUrl });
      }
    } catch (error) {
      console.error('Sayfa hero görseli yüklenemedi:', error);
    } finally {
      setHeroImageUploading(false);
    }
  };

  const handleAddPage = () => {
    if (!configId) {
      setSnack({ open: true, type: 'error', message: 'Önce e-ticaret yapılandırmasını kaydedin.' });
      return;
    }
    const newPage: PageItem = {
      id: `new_${Date.now()}`,
      configId,
      title: 'Yeni Sayfa',
      slug: '',
      content: '<p>Sayfa içeriğinizi buraya yazın.</p>',
      metaTitle: '',
      metaDescription: '',
      isActive: true,
      displayOrder: pages.length,
      showInMenu: false,
      switcherGroup: '',
      subtitle: '',
      bannerImageUrl: '',
      bannerHeight: 380,
      isDirty: true,
      isNew: true
    };
    setPages((prev) => [...prev, newPage]);
    setActiveIndex(pages.length);
  };

  const handleSavePage = async () => {
    if (!activePage || !configId) return;
    if (!activePage.title.trim()) {
      setSnack({ open: true, type: 'error', message: 'Başlık zorunlu' });
      return;
    }

    const slug = activePage.slug.trim() || slugify(activePage.title);
    if (!slug) {
      setSnack({ open: true, type: 'error', message: 'Geçerli bir slug üretilemedi' });
      return;
    }

    const payload: CustomPageData = {
      id: activePage.isNew ? undefined : activePage.id,
      configId,
      title: activePage.title.trim(),
      subtitle: activePage.subtitle,
      bannerImageUrl: activePage.bannerImageUrl,
      bannerHeight: activePage.bannerHeight,
      slug,
      content: activePage.content,
      metaTitle: activePage.metaTitle?.trim() || undefined,
      metaDescription: activePage.metaDescription?.trim() || undefined,
      name: activePage.switcherGroup?.trim() || '',
      isActive: activePage.isActive,
      showInMenu: activePage.showInMenu,
      showInFooter: false,
      displayOrder: activePage.displayOrder
    };

    setSaving(true);
    try {
      const res = await saveCustomPage(payload);
      const saved = fromApi(res?.data ?? {}, companyId);
      setPages((prev) => prev.map((p, idx) => (idx === activeIndex ? { ...saved, isDirty: false, isNew: false } : p)));
      setSnack({ open: true, type: 'success', message: 'Sayfa kaydedildi' });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Sayfa kaydedilirken hata oluştu';
      setSnack({ open: true, type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.pageId) return;
    const pageId = deleteConfirm.pageId;
    setDeleteConfirm({ open: false, pageId: null, title: '' });

    const target = pages.find((p) => p.id === pageId);
    if (!target) return;

    if (target.isNew) {
      setPages((prev) => prev.filter((p) => p.id !== pageId));
      setActiveIndex(0);
      return;
    }

    try {
      await deleteCustomPage(pageId);
      setPages((prev) => {
        const next = prev.filter((p) => p.id !== pageId);
        setActiveIndex(Math.min(activeIndex, Math.max(0, next.length - 1)));
        return next;
      });
      setSnack({ open: true, type: 'success', message: 'Sayfa silindi' });
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Sayfa silinirken hata oluştu';
      setSnack({ open: true, type: 'error', message });
    }
  };

  const previewLink = useMemo(() => {
    if (!activePage) return '';
    const s = activePage.slug.trim() || slugify(activePage.title);
    return s ? `/pages/${s}` : '';
  }, [activePage]);

  const existingSwitcherGroups = useMemo(() => {
    const names = pages
      .map((p) => p.switcherGroup.trim())
      .filter((name) => name.length > 0);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [pages]);

  const switcherSiblings = useMemo(() => {
    if (!activePage?.showInMenu) return [];
    const group = activePage.switcherGroup.trim().toLocaleLowerCase('tr-TR');
    return pages
      .filter((p) => {
        if (!p.showInMenu) return false;
        return p.switcherGroup.trim().toLocaleLowerCase('tr-TR') === group;
      })
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [activePage, pages]);

  const insertMapMarker = () => {
    const rawUrl = mapDialog.url.trim();
    if (!rawUrl) {
      setSnack({ open: true, type: 'error', message: 'Harita linki giriniz.' });
      return;
    }
    const encodedUrl = encodeURIComponent(rawUrl);
    const safeLabel = rawUrl.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<p><span class="gts-map-placeholder" data-gts-map="${encodedUrl}" contenteditable="false">🗺️ Harita: ${safeLabel} [[map:${encodedUrl}]]</span></p>`;
    editorRef.current?.insertHtml(html);
    setMapDialog({ open: false, url: '' });
  };

  const resetImageCardsDialog = () => {
    imageCardsEditTargetRef.current = null;
    setImageCardsDialog(DEFAULT_IMAGE_CARDS_DIALOG);
    setProductPicker({ open: false });
    setImageLibraryPicker({ open: false, targetIndex: -1 });
  };

  const openImageCardsDialog = (layout: 'grid' | 'sideText' = 'grid') => {
    imageCardsEditTargetRef.current = null;
    setImageCardsDialog({
      open: true,
      isEdit: false,
      title: '',
      layout,
      sideTextImageAlign: 'left',
      cards: [
        layout === 'sideText'
          ? { ...DEFAULT_IMAGE_CARD, ...DEFAULT_SIDE_TEXT_IMAGE_SIZE }
          : { ...DEFAULT_IMAGE_CARD }
      ]
    });
  };

  const openImageCardsDialogForEdit = (encoded: string, placeholder: HTMLElement) => {
    try {
      const decoded = decodeURIComponent(encoded);
      const parsed = JSON.parse(decoded) as ImageCardsPayload;
      const cards = Array.isArray(parsed.cards) ? parsed.cards : [];
      if (cards.length === 0) {
        setSnack({ open: true, type: 'error', message: 'Kart bloğu okunamadı.' });
        return;
      }

      const layout = parsed.settings?.layout === 'sideText' ? 'sideText' : 'grid';
      imageCardsEditTargetRef.current = placeholder;
      setImageCardsDialog({
        open: true,
        isEdit: true,
        title: parsed.title || '',
        layout,
        sideTextImageAlign: parsed.settings?.sideTextImageAlign === 'right' ? 'right' : 'left',
        cards
      });
    } catch {
      setSnack({ open: true, type: 'error', message: 'Kart bloğu düzenlenemedi.' });
    }
  };

  const insertImageCardsMarker = () => {
    const cleanedCards = (imageCardsDialog.cards || [])
      .map((card) => ({
        imageUrl: card.imageUrl.trim(),
        title: card.title?.trim() || '',
        subtitle: card.subtitle?.trim() || '',
        description: card.description?.trim() || '',
        linkUrl: card.linkUrl?.trim() || '',
        widthPx: (() => {
          const value = Number(card.widthPx);
          if (Number.isFinite(value) && value > 0) return value;
          return imageCardsDialog.layout === 'sideText' ? DEFAULT_SIDE_TEXT_IMAGE_SIZE.widthPx : undefined;
        })(),
        heightPx: (() => {
          const value = Number(card.heightPx);
          if (Number.isFinite(value) && value > 0) return value;
          return imageCardsDialog.layout === 'sideText' ? DEFAULT_SIDE_TEXT_IMAGE_SIZE.heightPx : undefined;
        })()
      }))
      .filter((card) => Boolean(card.imageUrl));

    if (cleanedCards.length === 0) {
      setSnack({ open: true, type: 'error', message: 'En az bir görsel URL girmelisiniz.' });
      return;
    }

    if (imageCardsDialog.layout === 'sideText' && !cleanedCards.some((card) => card.title || card.description || card.subtitle)) {
      setSnack({ open: true, type: 'error', message: 'Görsel + Metin bloğu için başlık veya açıklama giriniz.' });
      return;
    }

    const payload: ImageCardsPayload = {
      title: imageCardsDialog.title.trim() || undefined,
      cards: cleanedCards,
      settings: {
        layout: imageCardsDialog.layout,
        sideTextImageAlign: imageCardsDialog.sideTextImageAlign
      }
    };

    const encoded = encodeURIComponent(JSON.stringify(payload));
    const html = buildImageCardsEditorPlaceholder(encoded, payload);
    const editTarget = imageCardsEditTargetRef.current;
    if (editTarget && editorRef.current) {
      editorRef.current.replaceImageCardsBlock(editTarget, html);
    } else {
      editorRef.current?.insertHtml(html);
    }
    resetImageCardsDialog();
  };

  const appendCardsFromProducts = (products: EcommerceProductLite[]) => {
    if (!Array.isArray(products) || products.length === 0) return;
    const nextCards: ImageCardItem[] = products.map((p) => ({
      imageUrl: String(p.imageUrl || '').trim(),
      title: p.name,
      subtitle: p.stockCode || '',
      linkUrl: `/products/${p.id}`,
      productId: p.id
    }));

    const validCards = nextCards.filter((c) => Boolean(c.imageUrl));
    if (validCards.length === 0) {
      setSnack({ open: true, type: 'error', message: 'Seçilen ürünlerde kullanılabilir görsel yok.' });
      return;
    }

    setImageCardsDialog((prev) => ({
      ...prev,
      cards: [...prev.cards, ...validCards]
    }));
  };

  // ⚠️ configId kontrolü (Hakkımızda ekranındaki uyarı patternine benzer)
  if (!configId) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ E-ticaret yapılandırması bulunamadı
          </Typography>
          <Typography variant="body2">
            Sayfa yönetimini kullanmak için önce <strong>Genel</strong> sekmesinden e-ticaret yapılandırmasını kaydedin.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  // İlk yükleme + hiç sayfa yoksa empty state göster
  const showEmptyState = !loading && pages.length === 0;

  return (
    <Paper elevation={3} sx={{ position: 'relative', zIndex: 1 }}>
      {/* Tab Header - trapez stil */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          px: 1,
          backgroundColor: theme.palette.grey[200]
        }}
      >
        {pages.length === 0 ? (
          <Box sx={{ flex: 1, py: 1.5, px: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Henüz sayfa oluşturmadınız. Sağdaki <strong>+</strong> butonuyla ilk sayfanızı ekleyin.
            </Typography>
          </Box>
        ) : (
          <StyledTabs
            value={activeIndex}
            onChange={(_, v) => setActiveIndex(v)}
            aria-label="Sayfalar sekmeleri"
            variant="scrollable"
            scrollButtons="auto"
          >
            {pages.map((p, idx) => (
              <StyledTab
                key={p.id}
                value={idx}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {!p.isActive && (
                      <Tooltip title="Pasif">
                        <VisibilityOffIcon fontSize="inherit" sx={{ opacity: 0.6 }} />
                      </Tooltip>
                    )}
                    {p.showInMenu && (
                      <Tooltip title={p.switcherGroup ? `Geçiş grubu: ${p.switcherGroup}` : 'Vitrin geçişinde'}>
                        <CompareArrowsIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                      </Tooltip>
                    )}
                    <span>{p.title || 'Adsız Sayfa'}</span>
                    {p.isDirty && (
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#f59e0b'
                        }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </StyledTabs>
        )}

        <Tooltip title="Yeni Sayfa Ekle">
          <IconButton size="small" sx={{ ml: 1 }} onClick={handleAddPage}>
            <AddIcon />
          </IconButton>
        </Tooltip>
        <GuideButton guideKey="ecommerce.pages" size="small" sx={{ ml: 0.5 }} />
      </Box>

      {/* Loading */}
      {loading && pages.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Hata */}
      {error && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Empty state */}
      {showEmptyState && !error && (
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              backgroundColor: '#f8fafc',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider'
            }}
          >
            <ArticleOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Henüz sayfa oluşturmadınız
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sitenizde göstermek istediğiniz özel sayfaları (örn. Franchise, Kariyer, Gizlilik Politikası) buradan
              yönetebilirsiniz. Burada oluşturduğunuz sayfalar <code>/pages/&#123;slug&#125;</code> URL'siyle yayınlanır
              ve <strong>Header Üst Menüsü</strong> "Ara" butonundan seçilebilir.
            </Typography>
            <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddPage}>
              Yeni Sayfa
            </Button>
          </Box>
        </Box>
      )}

      {/* Sayfa sekmeleri – her sayfa bir TabPanel */}
      {pages.map((page, idx) => (
        <TabPanel key={page.id} value={activeIndex} index={idx}>
          {/* Sayfa başlık + aksiyon barı */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
              <ArticleOutlinedIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              {page.title || 'Adsız Sayfa'} Ayarları
              {page.isDirty && (
                <Chip
                  size="small"
                  label="Kaydedilmedi"
                  color="warning"
                  variant="outlined"
                  sx={{ ml: 1.5, height: 22 }}
                />
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {previewLink && !page.isNew && (
                <Tooltip title="Sayfayı ön yüzde aç">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => window.open(previewLink, '_blank')}
                      disabled={!page.isActive}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Listeyi yenile">
                <span>
                  <IconButton onClick={() => fetchPages()} disabled={saving || loading} color="default">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Bu sayfayı sil">
                <IconButton
                  onClick={() => setDeleteConfirm({ open: true, pageId: page.id, title: page.title })}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Bölüm 1 – Genel Bilgiler */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                Genel Bilgiler
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Sayfa Başlığı"
                    value={page.title}
                    onChange={(e) => {
                      const nextTitle = e.target.value;
                      const wasAutoSlug = !slugTouched[page.id];
                      updateActivePage({
                        title: nextTitle,
                        slug: wasAutoSlug ? slugify(nextTitle) : page.slug
                      });
                    }}
                    variant="outlined"
                    placeholder="Örn. Franchise Başvurusu"
                    helperText="Bu başlık sekmede ve menülerde görünür."
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="URL Slug"
                    value={page.slug}
                    onChange={(e) => {
                      setSlugTouched((prev) => ({ ...prev, [page.id]: true }));
                      updateActivePage({ slug: slugify(e.target.value) });
                    }}
                    variant="outlined"
                    placeholder="franchise-basvurusu"
                    helperText={previewLink ? `Önizleme URL: ${previewLink}` : 'Başlıktan otomatik üretilir'}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                          <LinkIconAlias fontSize="small" />
                        </Box>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    label="Görüntülenme Sırası"
                    type="number"
                    value={page.displayOrder}
                    onChange={(e) => updateActivePage({ displayOrder: Number(e.target.value || 0) })}
                    variant="outlined"
                    helperText="Küçük değer önce görünür"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      pl: 1
                    }}
                  >
                    <Typography variant="body2">Durum:</Typography>
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={page.isActive}
                          onChange={(_, v) => updateActivePage({ isActive: v })}
                          color="success"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {page.isActive ? (
                            <VisibilityIcon fontSize="small" color="success" />
                          ) : (
                            <VisibilityOffIcon fontSize="small" color="warning" />
                          )}
                          <Typography variant="body2">{page.isActive ? 'Aktif' : 'Pasif'}</Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: page.isActive ? '#e8f5e8' : '#fff3cd',
                      borderRadius: 1,
                      height: '100%'
                    }}
                  >
                    <Typography variant="body2" color={page.isActive ? 'success.main' : 'warning.main'}>
                      {page.isActive
                        ? '✅ Bu sayfa ön yüzde yayında. Header menüsünden de seçilebilir.'
                        : '👁️ Bu sayfa pasif. /pages/slug üzerinden açılmaz ve menü picker’ında görünmez.'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Bölüm 1b – Vitrin geçiş butonları */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <CompareArrowsIcon sx={{ mr: 1, color: 'primary.main' }} />
                Vitrin Geçiş Butonları
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      pl: 1
                    }}
                  >
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          checked={page.showInMenu}
                          onChange={(_, v) => updateActivePage({ showInMenu: v })}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Sayfalar arası geçişte göster
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Açık olan sayfalar vitrinde birbirine geçer.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Geçiş grubu"
                    value={page.switcherGroup}
                    onChange={(e) => updateActivePage({ switcherGroup: e.target.value })}
                    variant="outlined"
                    disabled={!page.showInMenu}
                    placeholder="Boş = varsayılan grup"
                    helperText="Aynı grubu paylaşan sayfalar vitrinde birlikte listelenir. Ayrı kümeler için farklı ad yazın (ör. Kurumsal, Yasal)."
                  />
                  {existingSwitcherGroups.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                      <Chip
                        size="small"
                        label="Varsayılan"
                        variant={page.switcherGroup ? 'outlined' : 'filled'}
                        color={page.switcherGroup ? 'default' : 'primary'}
                        disabled={!page.showInMenu}
                        onClick={() => updateActivePage({ switcherGroup: '' })}
                      />
                      {existingSwitcherGroups.map((group) => (
                        <Chip
                          key={group}
                          size="small"
                          label={group}
                          variant={page.switcherGroup === group ? 'filled' : 'outlined'}
                          color={page.switcherGroup === group ? 'primary' : 'default'}
                          disabled={!page.showInMenu}
                          onClick={() => updateActivePage({ switcherGroup: group })}
                        />
                      ))}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px dashed',
                      borderColor: 'divider',
                      backgroundColor: '#f8fafc'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Vitrin önizlemesi
                    </Typography>
                    {page.showInMenu && switcherSiblings.length >= 2 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {switcherSiblings.map((sibling) => (
                          <Chip
                            key={sibling.id}
                            label={sibling.title || 'Adsız Sayfa'}
                            color={sibling.id === page.id ? 'primary' : 'default'}
                            variant={sibling.id === page.id ? 'filled' : 'outlined'}
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : page.showInMenu ? (
                      <Typography variant="body2" color="text.secondary">
                        Vitrinde görünmesi için aynı gruba en az bir sayfa daha ekleyin.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Bu sayfa geçiş grubunda değil. İstediğiniz sayfalarda anahtarı açıp kaydedin.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <TitleIcon sx={{ mr: 1, color: 'primary.main' }} />
                Hero
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Sayfanın en üstünde blogdaki gibi tam genişlik görsel. Başlık sayfa adından, alttaki metin görselin üzerine yazılır.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Görsel üzerindeki açıklama"
                    value={page.subtitle}
                    onChange={(e) => updateActivePage({ subtitle: e.target.value })}
                    variant="outlined"
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Yükseklik (px)"
                    type="number"
                    value={page.bannerHeight}
                    onChange={(e) => updateActivePage({ bannerHeight: Number(e.target.value) || 380 })}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Hero görseli"
                    value={page.bannerImageUrl}
                    onChange={(e) => updateActivePage({ bannerImageUrl: e.target.value })}
                    variant="outlined"
                    helperText="Görsel URL'si. Avantajlar ve Başvurular sayfalarında boş bırakılırsa örnek görsel kullanılır."
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      disabled={heroImageUploading}
                    >
                      {heroImageUploading ? 'Yükleniyor...' : 'Görsel yükle'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void uploadPageHeroImage(file);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {page.bannerImageUrl && (
                      <Box
                        component="img"
                        src={page.bannerImageUrl}
                        alt="Hero önizleme"
                        sx={{ height: 56, width: 96, objectFit: 'cover', borderRadius: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Bölüm 2 – SEO Ayarları */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <PublicIcon sx={{ mr: 1, color: 'primary.main' }} />
                SEO Ayarları
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Meta Title (SEO)"
                    value={page.metaTitle}
                    onChange={(e) => updateActivePage({ metaTitle: e.target.value })}
                    variant="outlined"
                    placeholder={page.title}
                    helperText="Tarayıcı sekmesinde ve Google sonuçlarında görünecek başlık. Boş bırakılırsa Sayfa Başlığı kullanılır."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Meta Description (SEO)"
                    value={page.metaDescription}
                    onChange={(e) => updateActivePage({ metaDescription: e.target.value })}
                    variant="outlined"
                    placeholder="Bu sayfayı kısa ve net biçimde tarif eden 150-160 karakterlik bir açıklama."
                    helperText={`${(page.metaDescription || '').length}/160 karakter`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Google Önizleme
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: '#fff',
                      fontFamily: 'Arial, sans-serif'
                    }}
                  >
                    <Typography sx={{ color: '#1a0dab', fontSize: '18px', mb: 0.25 }}>
                      {page.metaTitle?.trim() || page.title || 'Sayfa Başlığı'}
                    </Typography>
                    <Typography sx={{ color: '#006621', fontSize: '13px', mb: 0.5 }}>
                      {previewLink || '/pages/slug'}
                    </Typography>
                    <Typography sx={{ color: '#545454', fontSize: '13px' }}>
                      {page.metaDescription?.trim() ||
                        'Bu sayfa için bir meta açıklama girmediniz. Arama motorları içeriğin ilk satırlarını kullanabilir.'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Bölüm 3 – Sayfa İçeriği */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <EditNoteIcon sx={{ mr: 1, color: 'primary.main' }} />
                Sayfa İçeriği
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Zengin metin editörünü kullanarak başlık, liste, tablo, link ve görsel biçimlemesi ekleyebilirsiniz.
                  Tablo ikonundan tablo ekleyin; hücreye tıklayınca satır/sütun düzenleme çubuğu açılır. Sayfaya
                  form, harita veya görsel kartlar yerleştirmek için sağ üstteki araç butonlarını kullanın.
                </Typography>
              </Box>
              <RichTextEditor
                ref={editorRef}
                value={page.content}
                onChange={(html) => updateActivePage({ content: html })}
                onImageCardsEdit={openImageCardsDialogForEdit}
                extraToolbarItems={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Tooltip title="Bu sayfaya bir form ekle">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DynamicFormOutlinedIcon fontSize="small" />}
                          onClick={() => setFormPicker({ open: true })}
                          disabled={!configId}
                          sx={{ py: 0.25, ml: 0.5 }}
                        >
                          Form Ekle
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bu sayfaya harita ekle">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MapOutlinedIcon fontSize="small" />}
                          onClick={() => setMapDialog({ open: true, url: '' })}
                          disabled={!configId}
                          sx={{ py: 0.25 }}
                        >
                          Harita Ekle
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Görsel ve açıklama metnini yan yana göster">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ArticleOutlinedIcon fontSize="small" />}
                          onClick={() => openImageCardsDialog('sideText')}
                          disabled={!configId}
                          sx={{ py: 0.25 }}
                        >
                          Görsel + Metin
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Bu sayfaya görsel kartlar ekle">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CollectionsOutlinedIcon fontSize="small" />}
                          onClick={() => openImageCardsDialog('grid')}
                          disabled={!configId}
                          sx={{ py: 0.25 }}
                        >
                          Görsel Kartlar
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                }
              />
            </AccordionDetails>
          </Accordion>

          {/* Alt aksiyon barı */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              variant="outlined"
              onClick={() => setDeleteConfirm({ open: true, pageId: page.id, title: page.title })}
            >
              Sayfayı Sil
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => fetchPages()} disabled={saving || loading} startIcon={<RestartAltIcon />}>
                Sıfırla / Yenile
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SaveIcon />}
                disabled={saving}
                onClick={handleSavePage}
              >
                Kaydet
              </Button>
            </Box>
          </Box>

          {/* Durum Göstergesi */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: page.isActive ? '#e8f5e8' : '#fff3cd',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" color={page.isActive ? 'success.main' : 'warning.main'}>
              {page.isActive
                ? `✅ Bu sayfa /pages/${page.slug || slugify(page.title) || 'slug'} adresinde yayında ve header menüsünden seçilebilir.`
                : '👁️ Bu sayfa pasif. Yayına almak için Durum anahtarını açık konuma getirin.'}
            </Typography>
          </Box>
        </TabPanel>
      ))}

      {/* Form picker */}
      <FormPickerDialog
        open={formPicker.open}
        configId={configId}
        onClose={() => setFormPicker({ open: false })}
        onSelect={({ slug, title }) => {
          const safeTitle = (title || slug).replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const html = `<p><span class="gts-form-placeholder" data-gts-form="${slug}" contenteditable="false">📝 Form: ${safeTitle} [[form:${slug}]]</span></p>`;
          editorRef.current?.insertHtml(html);
        }}
      />

      <Dialog open={mapDialog.open} onClose={() => setMapDialog({ open: false, url: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Harita Linki Ekle</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Google Maps linkini girin. Kaydettiğinizde sayfada gömülü harita olarak gösterilecektir.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Harita Linki"
            placeholder="https://www.google.com/maps/..."
            value={mapDialog.url}
            onChange={(e) => setMapDialog((prev) => ({ ...prev, url: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialog({ open: false, url: '' })}>İptal</Button>
          <Button variant="contained" onClick={insertMapMarker}>
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={imageCardsDialog.open} onClose={resetImageCardsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {imageCardsDialog.isEdit
            ? imageCardsDialog.layout === 'sideText'
              ? 'Görsel + Metin Bloğu Düzenle'
              : 'Görsel Kartlar Düzenle'
            : imageCardsDialog.layout === 'sideText'
              ? 'Görsel + Metin Bloğu Ekle'
              : 'Görsel Kartlar Ekle'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {imageCardsDialog.layout === 'sideText'
              ? 'Görsel ve açıklama metni yan yana gösterilir. En az bir görsel ve başlık/açıklama giriniz.'
              : 'Bu sayfa için kartlı görsel alanını yapılandırın. En az bir görsel URL girmelisiniz.'}
          </DialogContentText>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={imageCardsDialog.layout === 'sideText' ? 6 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel>Düzen</InputLabel>
                <Select
                  label="Düzen"
                  value={imageCardsDialog.layout}
                  onChange={(e) =>
                    setImageCardsDialog((prev) => ({
                      ...prev,
                      layout: e.target.value as 'grid' | 'sideText'
                    }))
                  }
                >
                  <MenuItem value="sideText">Görsel + Metin (Yan Yana)</MenuItem>
                  <MenuItem value="grid">Görsel Kartlar (Izgara)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {imageCardsDialog.layout === 'sideText' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Görsel Konumu</InputLabel>
                  <Select
                    label="Görsel Konumu"
                    value={imageCardsDialog.sideTextImageAlign}
                    onChange={(e) =>
                      setImageCardsDialog((prev) => ({
                        ...prev,
                        sideTextImageAlign: e.target.value as 'left' | 'right'
                      }))
                    }
                  >
                    <MenuItem value="left">Görsel Solda</MenuItem>
                    <MenuItem value="right">Görsel Sağda</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          <TextField
            fullWidth
            label="Bölüm Başlığı (Opsiyonel)"
            placeholder="Örn. Lezzetlerimiz"
            value={imageCardsDialog.title}
            onChange={(e) => setImageCardsDialog((prev) => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {imageCardsDialog.cards.map((card, index) => (
              <Box
                key={`image_card_${index}`}
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: '#fafafa' }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={`Görsel URL #${index + 1}`}
                      placeholder="https://..."
                      value={card.imageUrl}
                      onChange={(e) =>
                        setImageCardsDialog((prev) => ({
                          ...prev,
                          cards: prev.cards.map((x, i) => (i === index ? { ...x, imageUrl: e.target.value } : x))
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setImageLibraryPicker({ open: true, targetIndex: index })}
                    >
                      Resim Kütüphanesinden Seç
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={imageCardsDialog.layout === 'sideText' ? 12 : 4}>
                    <TextField
                      fullWidth
                      label={imageCardsDialog.layout === 'sideText' ? 'Başlık' : 'Kart Başlığı (Opsiyonel)'}
                      placeholder={imageCardsDialog.layout === 'sideText' ? 'Örn. BÜYÜK HAVUZ' : ''}
                      value={card.title || ''}
                      onChange={(e) =>
                        setImageCardsDialog((prev) => ({
                          ...prev,
                          cards: prev.cards.map((x, i) => (i === index ? { ...x, title: e.target.value } : x))
                        }))
                      }
                    />
                  </Grid>
                  {imageCardsDialog.layout === 'sideText' ? (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          label="Açıklama Metni"
                          placeholder="Görselin yanında gösterilecek açıklama..."
                          value={card.description || ''}
                          onChange={(e) =>
                            setImageCardsDialog((prev) => ({
                              ...prev,
                              cards: prev.cards.map((x, i) => (i === index ? { ...x, description: e.target.value } : x))
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Görsel Genişliği (px)"
                          value={card.widthPx ?? DEFAULT_SIDE_TEXT_IMAGE_SIZE.widthPx}
                          inputProps={{ min: 120, max: 900, step: 10 }}
                          onChange={(e) =>
                            setImageCardsDialog((prev) => ({
                              ...prev,
                              cards: prev.cards.map((x, i) =>
                                i === index ? { ...x, widthPx: Number(e.target.value) || DEFAULT_SIDE_TEXT_IMAGE_SIZE.widthPx } : x
                              )
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Görsel Yüksekliği (px)"
                          value={card.heightPx ?? DEFAULT_SIDE_TEXT_IMAGE_SIZE.heightPx}
                          inputProps={{ min: 90, max: 700, step: 10 }}
                          onChange={(e) =>
                            setImageCardsDialog((prev) => ({
                              ...prev,
                              cards: prev.cards.map((x, i) =>
                                i === index ? { ...x, heightPx: Number(e.target.value) || DEFAULT_SIDE_TEXT_IMAGE_SIZE.heightPx } : x
                              )
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Editörde görselin sağ alt köşesinden sürükleyerek de boyutunu ayarlayabilirsiniz.
                        </Typography>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Alt Başlık (Opsiyonel)"
                        value={card.subtitle || ''}
                        onChange={(e) =>
                          setImageCardsDialog((prev) => ({
                            ...prev,
                            cards: prev.cards.map((x, i) => (i === index ? { ...x, subtitle: e.target.value } : x))
                          }))
                        }
                      />
                    </Grid>
                  )}
                  {imageCardsDialog.layout !== 'sideText' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Kart Linki (Opsiyonel)"
                        placeholder="https://..."
                        value={card.linkUrl || ''}
                        onChange={(e) =>
                          setImageCardsDialog((prev) => ({
                            ...prev,
                            cards: prev.cards.map((x, i) => (i === index ? { ...x, linkUrl: e.target.value } : x))
                          }))
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() =>
                setImageCardsDialog((prev) => ({
                  ...prev,
                  cards: [
                    ...prev.cards,
                    prev.layout === 'sideText'
                      ? { ...DEFAULT_IMAGE_CARD, ...DEFAULT_SIDE_TEXT_IMAGE_SIZE }
                      : { ...DEFAULT_IMAGE_CARD }
                  ]
                }))
              }
              startIcon={<AddIcon />}
            >
              Kart Ekle
            </Button>
            <Button
              variant="outlined"
              startIcon={<CollectionsOutlinedIcon />}
              onClick={() => setProductPicker({ open: true })}
            >
              Ürünlerden Ekle
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={resetImageCardsDialog}>İptal</Button>
            <Button variant="contained" onClick={insertImageCardsMarker}>
              {imageCardsDialog.isEdit ? 'Kaydet' : 'Sayfaya Ekle'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SimpleProductSelectionModal
        open={productPicker.open}
        onClose={() => setProductPicker({ open: false })}
        onSelect={(products) => {
          appendCardsFromProducts(products as EcommerceProductLite[]);
          setProductPicker({ open: false });
        }}
        companyId={companyId}
        selectedProductIds={[]}
        maxSelection={24}
      />

      <ImageLibrarySelectionModal
        open={imageLibraryPicker.open}
        onClose={() => setImageLibraryPicker({ open: false, targetIndex: -1 })}
        onSave={(selectedUrls) => {
          const selectedUrl = selectedUrls?.[0] || '';
          if (!selectedUrl || imageLibraryPicker.targetIndex < 0) {
            setImageLibraryPicker({ open: false, targetIndex: -1 });
            return;
          }
          setImageCardsDialog((prev) => ({
            ...prev,
            cards: prev.cards.map((card, idx) =>
              idx === imageLibraryPicker.targetIndex ? { ...card, imageUrl: selectedUrl } : card
            )
          }));
          setImageLibraryPicker({ open: false, targetIndex: -1 });
        }}
        companyId={companyId}
        productName="Görsel Kartlar"
        currentImageUrls={[]}
        productId={null}
        singleSelect
        maxSelection={1}
      />

      {/* Silme onay diyaloğu */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, pageId: null, title: '' })}>
        <DialogTitle>Sayfayı sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteConfirm.title || 'Bu sayfa'}</strong> kalıcı olarak silinecek. Sayfa header menüsünde
            kullanılıyorsa o link de geçersiz kalır. Devam etmek istiyor musunuz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, pageId: null, title: '' })}>İptal</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.type}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PagesSettings;
